import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { Card } from '@/shared/components/ui/card'
import { Textarea } from '@/shared/components/ui/textarea'
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/shared/components/ui/tooltip'
import { cn } from '@/shared/lib/utils'
import { Shot } from '@/features/shotlist/store/useShotListStore'
import {
    ArrowDown,
    ArrowUp,
    Camera,
    Check,
    Clapperboard,
    Clock,
    Film,
    Image as ImageIcon,
    Maximize2,
    MessageSquare,
    Pencil,
    Sparkles,
    Trash2,
    Wand2,
    Info,
    Scan,
    X,
    Upload,
} from 'lucide-react'
import React, { useState } from 'react'
import { StoryboardFrame as StoryboardFrameType } from '../types/storyboard.types'
import ReferenceImagePicker from './ReferenceImagePicker'

interface StoryboardFrameProps {
    frame: StoryboardFrameType
    shot?: Shot | null
    /** Override the displayed image URL (e.g. with a cache-buster after regeneration). Falls back to frame.imageUrl. */
    displayImageUrl?: string
    onViewImage?: (imageUrl: string) => void
    /** Re-generate just the image using the current visual description */
    onRegenerateImage?: () => void
    isRegeneratingImage?: boolean
    /** Re-generate the entire frame (description + image) with AI */
    onRegenerateFrame: () => void
    isRegeneratingFrame?: boolean
    onUploadImage?: (file: File) => void
    isUploadingImage?: boolean
    onUpdate: (frameId: string, updates: Partial<StoryboardFrameType>) => void
    onRemove: (frameId: string) => void
    onMoveUp?: () => void
    onMoveDown?: () => void
    isFirstFrame?: boolean
    isLastFrame?: boolean
    defaultEditing?: boolean
}

const storyboardFieldHelpers = {
    shotAction: 'Describe the action or story beat shown in this frame.',
    visualDescription:
        'Add visual direction that improves the generated image, like mood, lighting, composition, styling, or atmosphere.',
    imagePromptOverride:
        'Optional override. If provided, the backend uses this prompt instead of generating one from the frame details.',
    dialogueNotes: 'Optional dialogue or performance context tied to this frame.',
}

const DEFAULT_REFERENCE_IMAGE_USAGE = 'General visual guidance'

const StoryboardFrame: React.FC<StoryboardFrameProps> = ({
    frame,
    shot,
    displayImageUrl,
    onViewImage,
    onRegenerateImage,
    isRegeneratingImage,
    onRegenerateFrame,
    isRegeneratingFrame,
    onUploadImage,
    isUploadingImage,
    onUpdate,
    onRemove,
    onMoveUp,
    onMoveDown,
    isFirstFrame = false,
    isLastFrame = false,
    defaultEditing = false,
}) => {
    const fileInputRef = React.useRef<HTMLInputElement>(null)
    const imageSrc = displayImageUrl ?? frame.imageUrl
    // const { updateFrame, removeFrame } = useStoryboardStore() // Removed
    const [isEditing, setIsEditing] = useState(defaultEditing)
    const [formData, setFormData] = useState({
        shotDescription: frame.shotDescription,
        visualDescription: frame.visualDescription,
        imagePromptOverride: frame.imagePromptOverride || '',
        dialogueNotes: frame.dialogueNotes || '',
        referenceImageUrl: frame.referenceImageUrl || '',
        referenceImageUsage:
            frame.referenceImageUsage ||
            (frame.referenceImageUrl ? DEFAULT_REFERENCE_IMAGE_USAGE : ''),
    })

    const handleSave = () => {
        onUpdate(frame.id, formData)
        setIsEditing(false)
    }

    const handleCancel = () => {
        setFormData({
            shotDescription: frame.shotDescription,
            visualDescription: frame.visualDescription,
            imagePromptOverride: frame.imagePromptOverride || '',
            dialogueNotes: frame.dialogueNotes || '',
            referenceImageUrl: frame.referenceImageUrl || '',
            referenceImageUsage:
                frame.referenceImageUsage ||
                (frame.referenceImageUrl ? DEFAULT_REFERENCE_IMAGE_USAGE : ''),
        })
        setIsEditing(false)
    }

    const handleDelete = () => {
        if (window.confirm('Are you sure you want to delete this frame?')) {
            onRemove(frame.id)
        }
    }

    const renderFieldWithTooltip = (
        label: string,
        helperText: string,
        children: React.ReactNode
    ) => (
        <div className="space-y-1">
            <div className="flex items-center gap-1.5">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    {label}
                </label>
                <TooltipProvider>
                    <Tooltip delayDuration={300}>
                        <TooltipTrigger asChild>
                            <Info className="h-3 w-3 cursor-help text-muted-foreground/50 transition-colors hover:text-primary" />
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-[220px] text-xs">
                            {helperText}
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            </div>
            {children}
        </div>
    )

    if (isEditing) {
        return (
            <Card className="overflow-hidden border-primary/50 shadow-lg relative bg-muted/20">
                <div className="flex flex-col h-full">
                    <div className="flex-1 p-5 flex flex-col justify-between">
                        <div className="space-y-4">
                            <div className="flex items-center gap-2 border-b border-border/40 pb-4">
                                <Badge variant="secondary" className="font-mono">
                                    Frame #{frame.frameNumber} (Editing)
                                </Badge>
                            </div>

                            <div className="flex gap-4 flex-col md:flex-row">
                                <div className="w-full md:w-1/3 space-y-3">
                                    {frame.imageUrl && (
                                        <div className="space-y-1">
                                            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                                Current Image
                                            </label>
                                            <div className="rounded-lg overflow-hidden border border-border bg-black/5">
                                                <img
                                                    src={frame.imageUrl}
                                                    alt={frame.shotDescription}
                                                    className="block w-full h-auto object-contain"
                                                />
                                            </div>
                                        </div>
                                    )}

                                    <ReferenceImagePicker
                                        value={formData.referenceImageUrl || undefined}
                                        usage={
                                            formData.referenceImageUsage ||
                                            DEFAULT_REFERENCE_IMAGE_USAGE
                                        }
                                        onChange={(url) =>
                                            setFormData((prev) => ({
                                                ...prev,
                                                referenceImageUrl: url ?? '',
                                                referenceImageUsage: url
                                                    ? prev.referenceImageUsage ||
                                                      DEFAULT_REFERENCE_IMAGE_USAGE
                                                    : '',
                                            }))
                                        }
                                        onUsageChange={(usage) =>
                                            setFormData((prev) => ({
                                                ...prev,
                                                referenceImageUsage: usage,
                                            }))
                                        }
                                    />

                                    {renderFieldWithTooltip(
                                        'Visual Description',
                                        storyboardFieldHelpers.visualDescription,
                                        <Textarea
                                            value={formData.visualDescription}
                                            onChange={(e) =>
                                                setFormData((prev) => ({
                                                    ...prev,
                                                    visualDescription: e.target.value,
                                                }))
                                            }
                                            className="min-h-[80px] text-sm"
                                            placeholder="Lighting, mood, composition, styling, environment..."
                                        />
                                    )}
                                </div>

                                <div className="flex-1 space-y-4">
                                    {renderFieldWithTooltip(
                                        'Shot Action',
                                        storyboardFieldHelpers.shotAction,
                                        <Textarea
                                            value={formData.shotDescription}
                                            onChange={(e) =>
                                                setFormData((prev) => ({
                                                    ...prev,
                                                    shotDescription: e.target.value,
                                                }))
                                            }
                                            className="min-h-[60px]"
                                            placeholder="Describe the action..."
                                        />
                                    )}

                                    <div className="rounded-md border border-border/50 bg-muted/20 px-3 py-3">
                                        <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                                            Shot Details
                                        </p>
                                        <div className="grid gap-3 md:grid-cols-4">
                                            <div>
                                                <p className="text-[11px] text-muted-foreground">
                                                    Camera Angle
                                                </p>
                                                <p className="text-sm font-medium">
                                                    {shot?.cameraAngle || 'Not set'}
                                                </p>
                                            </div>
                                            <div>
                                                <p className="text-[11px] text-muted-foreground">
                                                    Camera Movement
                                                </p>
                                                <p className="text-sm font-medium">
                                                    {shot?.cameraMovement || 'Not set'}
                                                </p>
                                            </div>
                                            <div>
                                                <p className="text-[11px] text-muted-foreground">
                                                    Duration
                                                </p>
                                                <p className="text-sm font-medium">
                                                    {shot?.duration || 'Not set'}
                                                </p>
                                            </div>
                                            <div>
                                                <p className="text-[11px] text-muted-foreground">
                                                    Framing
                                                </p>
                                                <p className="text-sm font-medium">
                                                    {shot?.framing || 'Not set'}
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    {renderFieldWithTooltip(
                                        'Image Prompt Override',
                                        storyboardFieldHelpers.imagePromptOverride,
                                        <Textarea
                                            value={formData.imagePromptOverride}
                                            onChange={(e) =>
                                                setFormData((prev) => ({
                                                    ...prev,
                                                    imagePromptOverride: e.target.value,
                                                }))
                                            }
                                            className="min-h-[72px] text-sm"
                                            placeholder="Optional direct AI prompt override..."
                                        />
                                    )}

                                    {renderFieldWithTooltip(
                                        'Dialogue Notes',
                                        storyboardFieldHelpers.dialogueNotes,
                                        <Textarea
                                            value={formData.dialogueNotes}
                                            onChange={(e) =>
                                                setFormData((prev) => ({
                                                    ...prev,
                                                    dialogueNotes: e.target.value,
                                                }))
                                            }
                                            className="min-h-[40px] text-sm"
                                            placeholder="Any dialogue associated with this frame..."
                                        />
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center justify-end gap-2 mt-6 pt-4 border-t border-border/40">
                            <Button size="sm" variant="ghost" onClick={handleCancel}>
                                <X className="h-4 w-4 mr-1" />
                                Cancel
                            </Button>
                            <Button size="sm" onClick={handleSave}>
                                <Check className="h-4 w-4 mr-1" />
                                Save Changes
                            </Button>
                        </div>
                    </div>
                </div>
            </Card>
        )
    }

    return (
        <Card className="overflow-visible hover:shadow-lg transition-all group border-border/50 hover:border-primary/30 relative">
            <div className="flex flex-col h-full w-full">
                <div className="border-b border-border/40 px-4 pt-4 pb-3 sm:px-5 sm:pt-5 sm:pb-4">
                    <div className="flex items-start gap-2 sm:items-center">
                        <span className="text-xs font-mono text-muted-foreground font-bold sm:text-sm">
                            #{frame.frameNumber}
                        </span>
                        <h3
                            className="font-semibold text-base leading-tight line-clamp-2 sm:text-lg"
                            title={frame.shotDescription}
                        >
                            {frame.shotDescription || (
                                <span className="text-muted-foreground italic">No Description</span>
                            )}
                        </h3>
                    </div>
                </div>

                <div className="grid gap-4 p-4 sm:gap-5 sm:p-5 xl:grid-cols-[minmax(320px,420px)_minmax(0,1fr)] xl:items-start">
                    <div className="flex flex-col gap-0 flex-shrink-0">
                        <div className="bg-black/5 relative group/image rounded-lg border border-border/40 overflow-visible">
                            {imageSrc ? (
                                <div className="relative w-full rounded-lg overflow-hidden">
                                    <img
                                        src={imageSrc}
                                        alt={frame.shotDescription}
                                        className="block w-full h-auto object-contain"
                                    />
                                    {onViewImage && (
                                        <button
                                            onClick={() => onViewImage?.(imageSrc!)}
                                            title="Expand image"
                                            className="absolute top-2 right-2 z-10 bg-black/50 hover:bg-black/80 text-white rounded-md p-1.5 opacity-100 sm:opacity-0 sm:group-hover/image:opacity-100 transition-opacity"
                                        >
                                            <Maximize2 className="h-3.5 w-3.5" />
                                        </button>
                                    )}

                                    {(onRegenerateImage || onUploadImage) && (
                                        <div className="absolute bottom-0 inset-x-0 opacity-100 sm:opacity-0 sm:group-hover/image:opacity-100 transition-opacity flex">
                                            {onRegenerateImage && (
                                                <button
                                                    onClick={onRegenerateImage}
                                                    disabled={isRegeneratingImage || isUploadingImage}
                                                    className="flex-1 flex items-center justify-center gap-1.5 py-3 sm:py-4 bg-black/60 hover:bg-black/80 text-white text-xs font-medium transition-colors disabled:opacity-60"
                                                >
                                                    <Wand2
                                                        className={cn(
                                                            'h-3.5 w-3.5',
                                                            isRegeneratingImage && 'animate-spin'
                                                        )}
                                                    />
                                                    {isRegeneratingImage
                                                        ? 'Regenerating...'
                                                        : 'Regenerate'}
                                                </button>
                                            )}
                                            {onUploadImage && (
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        fileInputRef.current?.click();
                                                    }}
                                                    disabled={isRegeneratingImage || isUploadingImage}
                                                    className="flex-1 border-l border-white/10 flex items-center justify-center gap-1.5 py-3 sm:py-4 bg-black/70 hover:bg-black/90 text-white text-xs font-medium transition-colors disabled:opacity-60"
                                                >
                                                    <Upload
                                                        className={cn(
                                                            'h-3.5 w-3.5',
                                                            isUploadingImage && 'animate-pulse'
                                                        )}
                                                    />
                                                    {isUploadingImage ? 'Uploading...' : 'Upload'}
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="w-full min-h-[225px] flex flex-col items-center justify-center bg-muted/30 p-6 text-center gap-3">
                                    <ImageIcon className="h-10 w-10 text-muted-foreground/40" />
                                    <p className="text-xs text-muted-foreground line-clamp-3 italic max-w-[200px]">
                                        "{frame.visualDescription}"
                                    </p>
                                    <div className="flex flex-wrap items-center justify-center gap-2 mt-1">
                                        {onRegenerateImage && (
                                            <button
                                                onClick={onRegenerateImage}
                                                disabled={isRegeneratingImage || isUploadingImage}
                                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-primary/10 hover:bg-primary/20 text-primary text-xs font-medium transition-colors disabled:opacity-60"
                                            >
                                                <Wand2
                                                    className={cn(
                                                        'h-3.5 w-3.5',
                                                        isRegeneratingImage && 'animate-spin'
                                                    )}
                                                />
                                                {isRegeneratingImage
                                                    ? 'Generating...'
                                                    : 'Generate'}
                                            </button>
                                        )}
                                        {onUploadImage && (
                                            <button
                                                onClick={() => fileInputRef.current?.click()}
                                                disabled={isRegeneratingImage || isUploadingImage}
                                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-secondary border border-secondary hover:bg-secondary/80 text-secondary-foreground text-xs font-medium transition-colors disabled:opacity-60"
                                            >
                                                <Upload
                                                    className={cn(
                                                        'h-3.5 w-3.5',
                                                        isUploadingImage && 'animate-pulse'
                                                    )}
                                                />
                                                {isUploadingImage
                                                    ? 'Uploading...'
                                                    : 'Upload Image'}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )}
                            {/* Hidden File Input */}
                            <input
                                type="file"
                                ref={fileInputRef}
                                className="hidden"
                                accept="image/*"
                                onChange={(e) => {
                                    const file = e.target.files?.[0]
                                    if (file && onUploadImage) {
                                        onUploadImage(file)
                                        e.target.value = ''
                                    }
                                }}
                            />
                        </div>
                        {frame.referenceImageUrl && (
                            <div className="pl-2 pt-2 sm:pl-3">
                                <div className="relative inline-block group/ref -mt-2">
                                    <div className="relative h-14 w-14 sm:h-16 sm:w-16 bg-white p-1 shadow-2xl rounded-sm rotate-3 group-hover/ref:rotate-0 group-hover/ref:scale-105 transition-all duration-300 ring-1 ring-black/5">
                                        <div className="absolute -left-2 top-1/2 -translate-y-1/2 w-3 h-8 bg-white/40 backdrop-blur-sm border border-white/20 -rotate-12 z-20 shadow-sm" />

                                        <div className="relative h-full w-full overflow-hidden bg-muted">
                                            <img
                                                src={frame.referenceImageUrl}
                                                alt="Reference Mood"
                                                className="h-full w-full object-cover"
                                                referrerPolicy="no-referrer"
                                            />
                                            <div className="absolute inset-0 bg-black/10 group-hover/ref:bg-black/0 transition-colors" />
                                        </div>

                                        <div className="absolute -top-1 -right-2 bg-primary px-1.5 py-0.5 rounded-sm shadow-sm -rotate-3 z-30">
                                            <p className="text-[8px] text-white font-bold uppercase tracking-widest leading-none">
                                                #Ref
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="flex flex-col justify-between w-full min-w-0">
                        <div className="space-y-5">
                            <div className="space-y-4 sm:space-y-5 lg:grid lg:gap-5 xl:grid-cols-[minmax(0,1fr)_max-content] xl:items-start">
                                <div className="space-y-4 sm:space-y-5">
                                    <div className="space-y-1">
                                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                            Visual Description
                                        </p>
                                        <p className="text-sm text-muted-foreground leading-relaxed min-h-[1.25rem]">
                                            <span className="italic">
                                                {frame.visualDescription
                                                    ? `"${frame.visualDescription}"`
                                                    : 'No visual description'}
                                            </span>
                                        </p>
                                    </div>

                                    <div className="space-y-1">
                                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                            Image Prompt Override
                                        </p>
                                        <p className="text-sm text-muted-foreground leading-relaxed">
                                            {frame.imagePromptOverride ? (
                                                frame.imagePromptOverride
                                            ) : (
                                                <span className="italic">Not set</span>
                                            )}
                                        </p>
                                    </div>

                                    <div className="space-y-1">
                                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                            Dialogue Notes
                                        </p>
                                        {frame.dialogueNotes ? (
                                            <p className="text-sm text-muted-foreground italic leading-relaxed">
                                                "{frame.dialogueNotes}"
                                            </p>
                                        ) : (
                                            <p className="text-sm text-muted-foreground italic">
                                                Not set
                                            </p>
                                        )}
                                    </div>
                                </div>

                                <div className="w-full max-w-full rounded-md border border-border/50 bg-muted/20 px-3 py-3 justify-self-start xl:w-fit">
                                    <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground hidden xl:block">
                                        Shot Details
                                    </p>
                                    <div className="flex flex-wrap gap-2 xl:block xl:space-y-2.5">
                                        <div className="flex items-center gap-1.5 rounded-md bg-muted/50 px-2 py-1.5 text-[11px] whitespace-nowrap">
                                            <Clapperboard className="h-3 w-3 shrink-0 text-muted-foreground" />
                                            <span className="font-medium text-muted-foreground">
                                                Shot:
                                            </span>
                                            <span className="font-medium">
                                                {shot?.shotType || 'Not set'}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-1.5 rounded-md bg-muted/50 px-2 py-1.5 text-[11px] whitespace-nowrap">
                                            <Scan className="h-3 w-3 shrink-0 text-muted-foreground" />
                                            <span className="font-medium text-muted-foreground">
                                                Framing:
                                            </span>
                                            <span className="font-medium">
                                                {shot?.framing || 'Not set'}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-1.5 rounded-md bg-muted/50 px-2 py-1.5 text-[11px] whitespace-nowrap">
                                            <Camera className="h-3 w-3 shrink-0 text-muted-foreground" />
                                            <span className="font-medium text-muted-foreground">
                                                Angle:
                                            </span>
                                            <span className="font-medium">
                                                {shot?.cameraAngle || 'Not set'}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-1.5 rounded-md bg-muted/50 px-2 py-1.5 text-[11px] whitespace-nowrap">
                                            <Film className="h-3 w-3 shrink-0 text-muted-foreground" />
                                            <span className="font-medium text-muted-foreground">
                                                Move:
                                            </span>
                                            <span className="font-medium">
                                                {shot?.cameraMovement || 'Not set'}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-1.5 rounded-md bg-muted/50 px-2 py-1.5 text-[11px] whitespace-nowrap">
                                            <Clock className="h-3 w-3 shrink-0 text-muted-foreground" />
                                            <span className="font-medium text-muted-foreground">
                                                Duration:
                                            </span>
                                            <span className="font-medium">
                                                {shot?.duration || 'Not set'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="mt-6 flex flex-wrap items-center justify-start gap-2 border-t border-border/40 pt-4 sm:justify-end">
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={onMoveUp}
                                disabled={!onMoveUp || isFirstFrame}
                                title="Move Up"
                            >
                                <ArrowUp className="h-4 w-4" />
                            </Button>
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={onMoveDown}
                                disabled={!onMoveDown || isLastFrame}
                                title="Move Down"
                            >
                                <ArrowDown className="h-4 w-4" />
                            </Button>

                            {onRegenerateFrame && (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={onRegenerateFrame}
                                    disabled={isRegeneratingFrame}
                                >
                                    <Sparkles
                                        className={cn(
                                            'h-4 w-4 mr-2 transition-transform duration-300',
                                            isRegeneratingFrame && 'animate-pulse'
                                        )}
                                    />
                                    {isRegeneratingFrame ? 'Regenerating...' : 'Regenerate Frame'}
                                </Button>
                            )}
                            <Button variant="ghost" size="sm" onClick={() => setIsEditing(true)}>
                                <Pencil className="h-4 w-4 mr-2" />
                                Edit
                            </Button>

                            <Button
                                variant="ghost"
                                size="sm"
                                className="text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                                onClick={handleDelete}
                                title="Remove Frame"
                            >
                                <Trash2 className="h-4 w-4" />
                                <span className="sr-only">Delete</span>
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </Card>
    )
}

export default StoryboardFrame
